import { CommonModule } from '@angular/common'
import {
  ChangeDetectionStrategy,
  Component,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core'
import { FormBuilder, FormControl, ReactiveFormsModule, Validators } from '@angular/forms'

import { throwError } from 'rxjs'
import { catchError, tap } from 'rxjs/operators'

import {
  FlxButtonComponent,
  FlxErrorComponent,
  FlxHelperComponent,
  FlxInputComponent,
  FlxInputDirective,
  FlxLabelComponent,
} from '@flowx/angular-ui-toolkit'
import { AccessManagementStore } from '@flowx/designer/modules/workspaces/shared/store/access-management.stores'
import {
  PASSWORD_ERRORS,
  PASSWORD_HELPER_TEXT,
  UsersType,
} from '@flowx/designer/shared/entities/utils'
import {
  ActionType,
  BackendErrorMessagesService,
} from '@flowx/designer/shared/services/backend-error-messages.service'
import { ToastMessageService } from '@flowx/designer/shared/services/toast-message.service'
import {
  PASSWORD_VALIDATORS,
  passwordMatchValidator,
  passwordNotSameAsEmailValidator,
} from '@flowx/designer/shared/validators'

@Component({
  selector: 'flx-change-password',
  templateUrl: 'change-password.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FlxErrorComponent,
    FlxHelperComponent,
    FlxInputDirective,
    ReactiveFormsModule,
    FlxLabelComponent,
    FlxInputComponent,
    CommonModule,
    FlxButtonComponent,
  ],
})
export class ChangePasswordComponent {
  #accessManagementStore = inject(AccessManagementStore)
  #toastMessageService = inject(ToastMessageService)

  userId = input.required<string>()
  email = input.required<string>()
  usersType = input.required<UsersType>()

  passwordErrors = PASSWORD_ERRORS
  passwordHelperText = PASSWORD_HELPER_TEXT

  passwordFC = new FormControl('', PASSWORD_VALIDATORS)
  confirmPasswordFC = new FormControl('', [Validators.required])
  closeModal = output()

  changePasswordFG = inject(FormBuilder).group(
    {
      email: new FormControl({ value: '', disabled: true }),
      password: this.passwordFC,
      confirmPassword: this.confirmPasswordFC,
    },
    {
      validator: [
        passwordMatchValidator('password', 'confirmPassword'),
        passwordNotSameAsEmailValidator('email', 'password'),
      ],
    }
  )

  submitted = signal(false)
  showPassword = signal(false)
  showConfirmPassword = signal(false)

  constructor() {
    effect(() => {
      const email = this.email()
      this.changePasswordFG.controls.email.setValue(email)
    })
  }

  submit(): void {
    this.submitted.set(true)

    if (this.changePasswordFG.invalid) {
      return
    }

    this.#accessManagementStore
      .updatePassword(this.userId(), this.passwordFC.value, this.usersType())
      .pipe(
        tap(() => {
          this.#toastMessageService.showSuccessMessage('Password update successful')
          this.closeModal.emit()
        }),
        catchError((error) => {
          const errorMessage = BackendErrorMessagesService.getErrorMessage(error.error, {
            entityName: 'password',
            type: ActionType.UPDATE,
          })

          this.#toastMessageService.showErrorMessage(errorMessage.title, errorMessage.message)
          return throwError(() => error)
        })
      )
      .subscribe()
  }
}
